import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import { useHistory } from 'react-router-dom';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import { DELETE_VANITY_PIECE, UPDATE_VANITY_PIECE, UPLOAD_VANITY_PIECE_ICON } from '../../../components/apolloQueries';
import { VanityPieceFragmentFragment } from '../../../../../../__gqltypes__';
import AssetDisplay from './AssetDisplay';
import DeleteVanityModal from './DeleteVanityModal';
import DragUpload from './DragUpload';

type Props = {
  vanityPiece: VanityPieceFragmentFragment;
  refetch: () => void;
};

/**
 * A vanity piece is the currency spent to unlock vanity items with the "Pieces" acquisition
 */
export default function VanityPieceView({ vanityPiece, refetch }: Props) {
  const history = useHistory();
  const [pieceData, setPieceData] = useState(vanityPiece);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    setPieceData(vanityPiece);
  }, [vanityPiece]);

  const [updateVanityPiece, { loading }] = useMutation(UPDATE_VANITY_PIECE, { onCompleted: () => refetch() });
  const [deleteVanityPiece, { loading: deleteLoading }] = useMutation(DELETE_VANITY_PIECE);
  const [uploadIcon, { loading: iconLoading }] = useMutation(UPLOAD_VANITY_PIECE_ICON, {
    onCompleted: () => refetch(),
  });

  const handleUpload = (file: Blob) => {
    uploadIcon({ variables: { input: { file, id: pieceData.id } } })
      .then(({ data }) => setPieceData(data.uploadVanityPieceIcon))
      .catch((error) => alert(error.message));
  };

  const handleUpdate = () => {
    if (!pieceData.name.trim().length) {
      alert('Please, enter valid name');
      return;
    }
    updateVanityPiece({
      variables: { input: { id: pieceData.id, name: pieceData.name.trim(), icon: pieceData.icon } },
    }).catch((error) => alert(error.message));
  };

  const handleDelete = () => {
    setShowDeleteModal(false);
    deleteVanityPiece({ variables: { vanityPieceId: pieceData.id } })
      .then(() => {
        refetch();
        history.push('/sp3/liveops/items/vanityPieces');
      })
      // VANITY_PIECE_IN_USE lists the items still unlocked with this piece
      .catch((error) => alert(error.message));
  };

  return (
    <Card className="mt-3">
      <LoadingLogo show={loading || deleteLoading || iconLoading} />
      <DeleteVanityModal
        type="Vanity Piece"
        name={pieceData?.name}
        show={showDeleteModal && !loading}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
      />
      <Card.Header className="text-center d-flex justify-content-between">
        <h4>{pieceData?.name}</h4>
        <div>
          <Button className="mr-3" variant="danger" onClick={() => setShowDeleteModal(true)}>
            Delete
          </Button>
          <Button onClick={handleUpdate}>Save</Button>
        </div>
      </Card.Header>
      <Card.Body>
        <Form>
          <Form.Group>
            <Form.Label>Id</Form.Label>
            <Form.Control placeholder={pieceData?.id} disabled />
          </Form.Group>
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={pieceData?.name ?? ''}
              maxLength={36}
              onChange={(e) => setPieceData({ ...pieceData, name: e.target.value })}
            />
          </Form.Group>
          <hr />
          <DragUpload onUpload={handleUpload} />
          <Form.Group>
            <Form.Label>Icon</Form.Label>
            <Form.Control value={pieceData?.icon ?? ''} disabled />
          </Form.Group>
          {!!pieceData?.icon?.length && <AssetDisplay assetUrl={pieceData.icon} />}
        </Form>
      </Card.Body>
    </Card>
  );
}
